import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { loadGoogleMaps, MapsLoaderError } from '../../utils/mapsLoader';

/**
 * Read-only map view. Renders a non-interactive Google Map centered on the
 * supplied lat/lng with a static marker. Used on public pages (e.g. the
 * Public Campaign view) to show a campaign's visit address on a map.
 *
 * Falls back gracefully when the Maps API key is missing or the script fails
 * to load — the component simply renders nothing so the surrounding layout is
 * unaffected.
 */

export interface AddressMapViewProps {
  latitude: number;
  longitude: number;
  zoom?: number;
  className?: string;
  ariaLabel?: string;
}

type LoaderStatus = 'loading' | 'ready' | 'unavailable';

interface LatLngLiteral {
  lat: number;
  lng: number;
}

interface MapLike {
  setCenter: (latLng: LatLngLiteral) => void;
  setZoom: (zoom: number) => void;
}

interface MarkerLike {
  setPosition: (latLng: LatLngLiteral) => void;
  setMap: (map: MapLike | null) => void;
}

interface MapsNamespaceLike {
  Map: new (el: HTMLElement, options: Record<string, unknown>) => MapLike;
  Marker: new (options: Record<string, unknown>) => MarkerLike;
}

const DEFAULT_ZOOM = 15;

const AddressMapView: React.FC<AddressMapViewProps> = ({
  latitude,
  longitude,
  zoom = DEFAULT_ZOOM,
  className = 'w-full h-60 md:h-72 rounded',
  ariaLabel,
}) => {
  const { t, i18n } = useTranslation('profile');
  const [status, setStatus] = useState<LoaderStatus>('loading');
  const mapRef = useRef<HTMLDivElement | null>(null);
  const mapInstanceRef = useRef<MapLike | null>(null);
  const markerRef = useRef<MarkerLike | null>(null);

  const language: 'ko' | 'en' = useMemo(
    () => (i18n.language?.startsWith('ko') ? 'ko' : 'en'),
    [i18n.language],
  );
  const region = language === 'ko' ? 'KR' : undefined;

  useEffect(() => {
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!apiKey) {
      setStatus('unavailable');
      return;
    }

    let cancelled = false;
    loadGoogleMaps({ apiKey, language, region })
      .then(() => {
        if (cancelled) return;
        setStatus('ready');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof MapsLoaderError) {
          console.warn(`[AddressMapView] Google Maps unavailable: ${err.code}`);
        } else {
          console.warn('[AddressMapView] Google Maps unavailable', err);
        }
        setStatus('unavailable');
      });

    return () => {
      cancelled = true;
    };
  }, [language, region]);

  // Initialize or update the map + marker whenever coords change.
  useEffect(() => {
    if (status !== 'ready') return;
    if (!mapRef.current) return;

    const win = window as unknown as { google?: { maps?: MapsNamespaceLike } };
    const maps = win.google?.maps;
    if (!maps) return;

    const center: LatLngLiteral = { lat: latitude, lng: longitude };

    if (!mapInstanceRef.current) {
      try {
        mapInstanceRef.current = new maps.Map(mapRef.current, {
          center,
          zoom,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          clickableIcons: false,
          gestureHandling: 'cooperative',
          draggable: false,
          disableDefaultUI: true,
          zoomControl: true,
        });
      } catch (err) {
        console.warn('[AddressMapView] Failed to initialize map', err);
        return;
      }
    } else {
      mapInstanceRef.current.setCenter(center);
      mapInstanceRef.current.setZoom(zoom);
    }

    if (markerRef.current) {
      markerRef.current.setPosition(center);
    } else {
      try {
        markerRef.current = new maps.Marker({
          position: center,
          map: mapInstanceRef.current,
          draggable: false,
        });
      } catch (err) {
        console.warn('[AddressMapView] Failed to create marker', err);
      }
    }
  }, [latitude, longitude, zoom, status]);

  useEffect(() => {
    return () => {
      if (markerRef.current) {
        markerRef.current.setMap(null);
        markerRef.current = null;
      }
      mapInstanceRef.current = null;
    };
  }, []);

  if (status === 'unavailable') {
    return null;
  }

  if (status === 'loading') {
    return (
      <div
        className={`${className} bg-gray-200 animate-pulse`}
        role="status"
        aria-label={t('addressPicker.loading')}
      />
    );
  }

  return (
    <div
      ref={mapRef}
      className={className}
      role="img"
      aria-label={ariaLabel ?? t('addressPicker.placeholder')}
    />
  );
};

export default AddressMapView;
